import React from "react";
import {
  Button,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { BsPlus, BsThreeDotsVertical } from "react-icons/bs";
import ratingScaleApi from "../api/ratingScaleApi";
import ModulePageShell from "../components/common/ModulePageShell";
import OperationalPanel from "../components/common/OperationalPanel";
import PerformanceLocalNav from "../components/common/PerformanceLocalNav";
import StatusBadge from "../components/common/StatusBadge";
import { CompactEmptyState, InlineError, PageSkeleton } from "../components/common/PageState";
import performanceQueryKeys from "../utils/queryKeys";
import { recordId, unwrapRows } from "../utils/normalize";

const RatingScalesPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const listQuery = useQuery({
    queryKey: performanceQueryKeys.ratingScales.list({ limit: 100 }),
    queryFn: () => ratingScaleApi.getPerformanceRatingScales({ limit: 100 }),
  });
  const rows = unwrapRows(listQuery.data);

  return (
    <ModulePageShell
      title="Rating scales"
      description="Manage tenant rating values used by appraisal templates."
      onRefresh={() => listQuery.refetch()}
      actions={
        <Button size="sm" colorScheme="blue" leftIcon={<BsPlus />} onClick={() => navigate("/performance/pms-format/rating-scales/create")}>
          Create scale
        </Button>
      }
    >
      <PerformanceLocalNav />
      <OperationalPanel>
        {listQuery.isLoading ? (
          <PageSkeleton rows={6} />
        ) : listQuery.isError ? (
          <InlineError message="Unable to load rating scales." />
        ) : rows.length === 0 ? (
          <CompactEmptyState title="No rating scales found." message="Create a rating scale before publishing a PMS template." />
        ) : (
          <TableContainer>
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Name</Th>
                  <Th>Code</Th>
                  <Th>Type</Th>
                  <Th>Range</Th>
                  <Th isNumeric>Values</Th>
                  <Th>Decimal support</Th>
                  <Th>Status</Th>
                  <Th isNumeric>Templates using scale</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {rows.map((scale) => {
                  const values = scale.values || scale.ratings || [];
                  const scaleId = recordId(scale);
                  return (
                    <Tr key={scaleId}>
                      <Td>
                        <Text fontWeight="semibold" fontSize="sm">{scale.name || "Untitled scale"}</Text>
                      </Td>
                      <Td>{scale.code || "-"}</Td>
                      <Td>{scale.type || "Numeric"}</Td>
                      <Td>{scale.minValue ?? values[0]?.value ?? "-"} - {scale.maxValue ?? values[values.length - 1]?.value ?? "-"}</Td>
                      <Td isNumeric>{values.length}</Td>
                      <Td>{scale.allowDecimals || scale.decimalSupport ? "Yes" : "No"}</Td>
                      <Td><StatusBadge value={scale.status || "ACTIVE"} /></Td>
                      <Td isNumeric>{scale.templateCount || scale.templatesUsingScale || 0}</Td>
                      <Td>
                        <Menu>
                          <MenuButton as={IconButton} size="sm" variant="ghost" icon={<BsThreeDotsVertical />} aria-label="Rating scale actions" />
                          <MenuList>
                            <MenuItem onClick={() => navigate(`/performance/pms-format/rating-scales/${scaleId}`)}>View</MenuItem>
                            <MenuItem onClick={() => navigate(`/performance/pms-format/rating-scales/${scaleId}/edit`)}>Edit</MenuItem>
                            <MenuItem
                              onClick={() =>
                                toast({
                                  title: "Editing warning",
                                  description: "Published templates using this scale should be reviewed before archiving it.",
                                  status: "warning",
                                  duration: 3000,
                                })
                              }
                            >
                              Archive
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </OperationalPanel>
    </ModulePageShell>
  );
};

export default RatingScalesPage;
